import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import {
  SentInvite, SharedWorkspace, SharedWorkspaceService, WorkspaceMember,
} from '../services/shared-workspace.service';

/**
 * One shared workspace: what is in it, who can read it, and who has been asked.
 *
 * The workspace is named by id throughout. Its key is never handled here - the
 * server resolves that from membership, and sending one from the browser would
 * be ignored.
 */
@Component({
  selector: 'app-shared-workspace-detail',
  standalone: false,
  templateUrl: './shared-workspace-detail.component.html',
  styleUrls: ['./shared-workspace-detail.component.css'],
})
export class SharedWorkspaceDetailComponent implements OnInit {

  workspaceId!: number;
  workspace: SharedWorkspace | null = null;
  isOwner = false;

  members: WorkspaceMember[] = [];
  invites: SentInvite[] = [];

  itemType: 'MEMORY' | 'RECIPE' = 'MEMORY';
  items: any[] = [];

  inviteEmail = '';
  loading = true;
  loadingItems = false;
  busy = false;

  constructor(
    private route: ActivatedRoute,
    private workspaces: SharedWorkspaceService,
    private toastr: ToastrService,
    public router: Router,
  ) { }

  ngOnInit(): void {
    this.workspaceId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      // There is no "get one workspace" endpoint on purpose - the list is
      // already scoped to what you may see, so finding it here cannot show you
      // a workspace you are not part of.
      const list = await this.workspaces.listWorkspaces();
      const owned = (list?.owned || []).find(w => w.workspaceId === this.workspaceId);
      const joined = (list?.sharedWithMe || []).find(w => w.workspaceId === this.workspaceId);

      this.workspace = owned || joined || null;
      this.isOwner = !!owned;

      if (!this.workspace) {
        this.toastr.error('That workspace is not available to you.');
        this.router.navigate(['/shared-workspaces']);
        return;
      }

      this.members = await this.workspaces.listMembers(this.workspaceId);
      if (this.isOwner) {
        this.invites = await this.workspaces.listInvites(this.workspaceId);
      }
      await this.loadItems();
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.loading = false;
    }
  }

  async loadItems(): Promise<void> {
    this.loadingItems = true;
    try {
      const res = await this.workspaces.listItems(this.workspaceId, this.itemType);
      this.items = this.extractItems(res);
    } catch (error: any) {
      this.toastr.error(error.message);
      this.items = [];
    } finally {
      this.loadingItems = false;
    }
  }

  async switchType(type: 'MEMORY' | 'RECIPE'): Promise<void> {
    if (this.itemType === type) {
      return;
    }
    this.itemType = type;
    await this.loadItems();
  }

  async sendInvite(): Promise<void> {
    const email = (this.inviteEmail || '').trim();
    if (!email) {
      this.toastr.error('Enter an email address.');
      return;
    }
    this.busy = true;
    try {
      const result = await this.workspaces.invite(this.workspaceId, email);
      if (result?.emailSent === false) {
        this.toastr.warning(
          `Invitation created for ${email}, but the email did not go out. Revoke and try again.`);
      } else {
        this.toastr.success(`Invitation sent to ${email}.`);
      }
      this.inviteEmail = '';
      this.invites = await this.workspaces.listInvites(this.workspaceId);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  async revoke(invite: SentInvite): Promise<void> {
    this.busy = true;
    try {
      await this.workspaces.revokeInvite(invite.inviteId);
      this.toastr.info(`Invitation to ${invite.email} withdrawn.`);
      this.invites = await this.workspaces.listInvites(this.workspaceId);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  async remove(member: WorkspaceMember): Promise<void> {
    if (!confirm(`Remove ${member.email}? They stop seeing what is shared here.`)) {
      return;
    }
    this.busy = true;
    try {
      await this.workspaces.removeMember(this.workspaceId, member.userId);
      this.toastr.info(`${member.email} removed.`);
      this.members = await this.workspaces.listMembers(this.workspaceId);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  // The shape of a memory page comes from XDB and is not ours to assume, so
  // take the first array of objects the response offers.
  private extractItems(res: any): any[] {
    if (!res || typeof res !== 'object') {
      return [];
    }
    for (const key of ['memories', 'result', 'results', 'items', 'data', 'records']) {
      if (Array.isArray(res[key])) {
        return res[key];
      }
    }
    const anyArray = Object.values(res).find(v => Array.isArray(v));
    return (anyArray as any[]) || [];
  }

  itemText(item: any): string {
    if (typeof item === 'string') {
      return item;
    }
    for (const key of ['content', 'statement', 'memory', 'text', 'summary', 'title']) {
      if (item && typeof item[key] === 'string' && item[key].length) {
        return item[key];
      }
    }
    return JSON.stringify(item);
  }

  formatDate(ts: number): string {
    if (!ts) {
      return '';
    }
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }
}
